import React from "react";
import Icon from "components/AppIcon";

const VEHICLE_LABELS = {
  "limited-partnership": "Limited Partnership",
  "llp": "LLP",
  "private-limited": "Private Limited",
  "unit-trust": "Unit Trust",
};

const JURISDICTION_LABELS = {
  "england-wales": "England & Wales",
  "scotland": "Scotland",
  "cayman-islands": "Cayman Islands",
  "luxembourg": "Luxembourg",
  "ireland": "Ireland",
  "jersey": "Jersey",
  "guernsey": "Guernsey",
  "delaware": "Delaware, USA",
  "singapore": "Singapore",
};

function SummaryRow({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-2 py-2 border-b last:border-b-0" style={{ borderColor: "var(--color-border)" }}>
      <span className="font-caption text-xs text-muted-foreground flex-shrink-0">{label}</span>
      <span
        className="font-caption text-xs font-semibold text-right"
        style={{ color: highlight ? "var(--color-primary)" : "var(--color-foreground)" }}
      >
        {value}
      </span>
    </div>
  );
}

export default function SummaryPanel({ data, currentStep }) {
  const targetSize = parseFloat(data?.targetSize) || 0;
  const investors = data?.investors || [];
  const totalCommitted = investors?.reduce((s, i) => s + (parseFloat(i?.commitment) || 0), 0);
  const commitPct = targetSize > 0 ? ((totalCommitted / targetSize) * 100)?.toFixed(1) : "0";
  const milestones = data?.milestones || [];

  const sections = [
    {
      title: "SPV Basics",
      icon: "Building2",
      step: 0,
      rows: [
        { label: "Name", value: data?.name || "—" },
        { label: "Jurisdiction", value: JURISDICTION_LABELS?.[data?.jurisdiction] || data?.jurisdiction || "—" },
        { label: "Vehicle", value: VEHICLE_LABELS?.[data?.vehicleType] || data?.vehicleType || "—" },
        { label: "Target Size", value: targetSize > 0 ? `£${targetSize?.toLocaleString("en-GB")}` : "—" },
        { label: "Target IRR", value: data?.targetIRR ? `${data?.targetIRR}%` : "—" },
      ],
    },
    {
      title: "Cap Table",
      icon: "Users",
      step: 1,
      rows: [
        { label: "Investors", value: investors?.length > 0 ? `${investors?.length} added` : "—" },
        { label: "Committed", value: totalCommitted > 0 ? `£${totalCommitted?.toLocaleString("en-GB")}` : "—" },
        { label: "Progress", value: `${commitPct}%`, highlight: parseFloat(commitPct) >= 100 },
        { label: "GP Commitment", value: data?.gpCommitment ? `£${parseFloat(data?.gpCommitment)?.toLocaleString("en-GB")}` : "—" },
      ],
    },
    {
      title: "Fees",
      icon: "Percent",
      step: 2,
      rows: [
        { label: "Mgmt Fee", value: data?.managementFee ? `${data?.managementFee}% p.a.` : "—" },
        { label: "Carry", value: data?.carry ? `${data?.carry}%` : "—" },
        { label: "Hurdle Rate", value: data?.hurdleRate ? `${data?.hurdleRate}%` : "—" },
        { label: "Platform Fee", value: data?.platformFee ? `${data?.platformFee}%` : "—" },
      ],
    },
    {
      title: "Timeline",
      icon: "Calendar",
      step: 3,
      rows: [
        { label: "First Close", value: data?.firstCloseDate ? new Date(data.firstCloseDate)?.toLocaleDateString("en-GB") : "—" },
        { label: "Final Close", value: data?.finalCloseDate ? new Date(data.finalCloseDate)?.toLocaleDateString("en-GB") : "—" },
        { label: "Fund Term", value: data?.fundTerm ? `${data?.fundTerm} years` : "—" },
        { label: "Milestones", value: milestones?.length > 0 ? `${milestones?.length} defined` : "—" },
      ],
    },
    {
      title: "Tokenisation",
      icon: "Coins",
      step: 4,
      rows: [
        { label: "Enabled", value: data?.tokenisationEnabled ? "Yes" : "No" },
        ...(data?.tokenisationEnabled ? [
          { label: "Token Standard", value: data?.tokenStandard || "TBD" },
          { label: "Secondary Trading", value: data?.secondaryTradingEnabled ? "Enabled" : "Disabled" },
          { label: "Restrictions", value: (data?.transferRestrictions || [])?.length > 0 ? `${(data?.transferRestrictions || [])?.length} applied` : "None" },
        ] : []),
      ],
    },
  ];

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b"
        style={{ background: "var(--color-muted)", borderColor: "var(--color-border)" }}
      >
        <div className="flex items-center gap-2">
          <Icon name="FileText" size={16} color="var(--color-primary)" />
          <span className="font-caption font-semibold text-sm text-foreground">SPV Summary</span>
        </div>
        <p className="font-caption text-xs text-muted-foreground mt-0.5">Live configuration overview</p>
      </div>
      {/* Sections */}
      <div className="divide-y" style={{ borderColor: "var(--color-border)" }}>
        {sections?.map((section) => (
          <div key={section?.title} className="px-4 py-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Icon
                name={section?.icon}
                size={13}
                color={currentStep === section?.step ? "var(--color-primary)" : "var(--color-muted-foreground)"}
              />
              <span
                className="font-caption text-xs font-semibold uppercase tracking-wide"
                style={{
                  color: currentStep === section?.step ? "var(--color-primary)" : "var(--color-muted-foreground)",
                  letterSpacing: "0.06em",
                }}
              >
                {section?.title}
              </span>
              {currentStep === section?.step && (
                <span
                  className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-caption font-medium"
                  style={{ background: "rgba(59,76,76,0.1)", color: "var(--color-primary)" }}
                >
                  Active
                </span>
              )}
            </div>
            {section?.rows?.map((row) => (
              <SummaryRow key={row?.label} label={row?.label} value={row?.value} highlight={row?.highlight} />
            ))}
          </div>
        ))}
      </div>
      {/* Completion indicator */}
      <div className="px-4 py-3 border-t" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-caption text-xs text-muted-foreground">Completion</span>
          <span className="font-data text-xs font-semibold" style={{ color: "var(--color-primary)" }}>
            {Math.round(((currentStep + 1) / 5) * 100)}%
          </span>
        </div>
        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-border)" }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.round(((currentStep + 1) / 5) * 100)}%`,
              background: "var(--color-primary)",
            }}
          />
        </div>
      </div>
    </div>
  );
}